import { ref, computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useAccontStore } from '@/stores/AccountStore.js'
import { Station } from '@/models/station.js'
import {
	dayStart,
} from '@/helpers.js'
import {
	resourceSchema,
	RESOURCE_NAMES,
	readResources,
	parseResource,
	convertToApiTimeDate,
} from '@/api/api.js'




export const lightningSchema = Object.freeze(Object.assign(
	{},
	resourceSchema,
	{
		location: Object.freeze({
			key: 'location',
			title: 'координати',

			type: Object.freeze({
				key: 'type',

				allowed: Object.freeze({
					point: 'Point'
				}),
			}),
			coordinates: Object.freeze({
				key: 'coordinates',

				lat: Object.freeze({
					key: 1,
					title: 'широта',
				}),
				lon: Object.freeze({
					key: 0,
					title: 'довгота',
				}),
			})
		}),

		flashId: Object.freeze({
			key: 'flash_id',
			title: '',
		}),

		flashTimeUTC: Object.freeze({
			key: 'flash_time_UTC',
			title: 'час',
		}),

		flashDurationMs: Object.freeze({
			key: 'flash_duration_ms',
			title: 'тривалість, мс',
		}),

		flashFootprintKm2: Object.freeze({
			key: 'flash_footprint_km2',
			title: 'площа, км^2',
		}),

		radianceScaled: Object.freeze({
			key: 'radiance_scaled',
			title: 'інтенсивність',
		}),

		numberOfEvents: Object.freeze({
			key: 'number_of_events',
			title: '',
		}),

		numberOfGroups: Object.freeze({
			key: 'number_of_groups',
			title: '',
		}),

		flashFilterConfidence: Object.freeze({
			key: 'flash_filter_confidence',
			title: '',
		}),
	},
))

// "Date" type pasport fields (flatten object keys)
const LIGHTNING_DATE_PATHS = [
	lightningSchema.updateTime.key,
	lightningSchema.createTime.key,
	lightningSchema.flashTimeUTC.key,
]




export function useLightnings() {
	// console.log('useLightnings call')
	const accountStore = useAccontStore()
	// Reactive lower bound for lightning fetch time window.
	const fromTime = ref(null)
	// Reactive upper bound for lightning fetch time window.
	const toTime = ref(null)
	const monitoringStations = ref(new Array()) // monitoring stations list

	const lightningsQuery = useQuery({
		// Include auth identity and time filter in key to auto-refetch on change
		queryKey: computed(() => [
			RESOURCE_NAMES.lightnings,
			...(fromTime.value == null ? [] : [convertToApiTimeDate(fromTime.value)]),
			...(toTime.value == null ? [] : [convertToApiTimeDate(toTime.value)]),
		]),
		queryFn: async () => {
			// If no filters (don't init)
			if (fromTime.value === null && toTime.value === null && monitoringStations.value.length == 0) {
				return []
			}
			// console.log('fetch Lightnings from DB', convertToApiTimeDate(fromTime.value), convertToApiTimeDate(toTime.value))
			const isTimeRange = fromTime.value !== null || toTime.value !== null
			let whereObject = {}

			if (isTimeRange) {
				whereObject[lightningSchema.flashTimeUTC.key] = {
					...(fromTime.value !== null && { "$gt": convertToApiTimeDate(fromTime.value) }),
					...(toTime.value !== null && { "$lt": convertToApiTimeDate(toTime.value) }),
				}
			}

			const res = await readResources(
				RESOURCE_NAMES.lightnings,
				accountStore.userLogin,
				accountStore.userPassword,
				[ `where=${JSON.stringify(whereObject)}` ],
			)

			return res.map((el) => parseResource(el, LIGHTNING_DATE_PATHS))
		},
		// Keep results warm to reduce repeated network requests.
		staleTime: 10 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
	})

	const allLightnings = computed(() => lightningsQuery.data.value ?? [])

	// Map flashId: lightning
	const lightningByFlashId = computed(() => 
		new Map(allLightnings.value.map((el) => [el[lightningSchema.flashId.key], el]))
	)

	function loadLightnings(newFromTime, newToTime) {
		// console.log('loadLightnings for stations', toRaw(monitoringStations.value))
		// Updating fromTime, toTime changes queryKey and triggers TanStack Query refetch.
		fromTime.value = new Date(newFromTime)
		toTime.value = newToTime === undefined ? dayStart(new Date()) : new Date(newToTime)
	}

	function addStationToMonitoring(station) {
		// If station is on monitoring list
		if (monitoringStations.value.some((item) => item.id === station.id)) {
			return
		}

		// console.log('Add Station To Monitoring', station.id)
		monitoringStations.value.push({
			id: station.id,
			lat: station.passport.get(Station.passportKeys.location)?.coordinates[1],
			lon: station.passport.get(Station.passportKeys.location)?.coordinates[0],
		})
	}

	return {
		allLightnings,
		lightningByFlashId,

		isLoading: lightningsQuery.isLoading,
		isError: lightningsQuery.isError,
		error: lightningsQuery.error,

		addStationToMonitoring,
		loadLightnings,
		refetch: lightningsQuery.refetch,
	}
}